/**
 * AI Copilot — Anthropic Messages API tool-use loop, single-agent design.
 *
 * State enters the model via the system prompt (deterministic markdown);
 * actions exit via tools (deterministic mutators). The chat surface stays
 * text-only — no vision in this scaffold (browser-side .toDataURL captures
 * of the 3D canvas would be a Phase 4d addition).
 *
 * The API key lives only on the server — this component POSTs to /api/copilot
 * which proxies to Anthropic with `x-api-key` set server-side. Same-origin in
 * prod (Traefik PathPrefix), Vite dev proxy on :5173 → :3001 in dev.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Send } from 'lucide-react';
import {
  COLLECTIONS, COLLECTION_BY_CODE, BASE_BY_CODE, SIZES_BY_COLLECTION,
  CHAIR_COLLECTIONS, CHAIR_SIZES,
} from '../data/catalog.js';
import { TOOLS, makeToolExecutor } from './tools.js';
import { t } from '../i18n.js';

const MODEL   = 'claude-sonnet-4-5';   // newest stable Sonnet for tool-use
const MAX_TOOL_ITERATIONS = 12;

// ─── State summary ─────────────────────────────────────────────────────────

function summariseItem(it, i, selected) {
  const sel = it.id === selected ? '[selected] ' : '';
  if (it.kind === 'table') {
    const col = COLLECTION_BY_CODE[it.collection]?.name || it.collection;
    const insert = it.insertOrProfile ? ` +${it.insertOrProfile}` : '';
    return `${i + 1}. ${sel}table id=${it.id}  ${col} · ${it.sizeKey} · base ${it.baseCode} · top ${it.topCode}${insert} · qty ${it.qty}`;
  }
  const col = CHAIR_COLLECTIONS.find(c => c.code === it.collection)?.name || it.collection;
  return `${i + 1}. ${sel}chair id=${it.id}  ${col} · ${it.sizeKey === 'B' ? 'with arms' : 'no arms'} · frame ${it.frameCode} · uphol ${it.upholsteryCode} · qty ${it.qty}`;
}

function buildStateSummary({ project, items, selectedItemId, total, fmtEUR }) {
  const meta = [
    `name=${project.name || '—'}`,
    `client=${project.client || '—'}`,
    `status=${project.status}`,
    `date=${project.date}`,
  ].join('  ·  ');
  const itemLines = items.length
    ? items.map((it, i) => summariseItem(it, i, selectedItemId)).join('\n')
    : '(no items yet)';
  return `# Current project state\n${meta}\n\nItems:\n${itemLines}\n\nTotal: ${fmtEUR(total)}`;
}

// Enumerate per-collection valid size keys for the system prompt so the model
// doesn't have to guess (e.g. KR has 200x106 not 200x100).
function buildSizeReference() {
  return Object.entries(SIZES_BY_COLLECTION)
    .map(([col, sizes]) => `  ${col}: ${sizes.map(s => s.key).join(', ')}`)
    .join('\n');
}

function buildSystemPrompt(stateSummary, lang) {
  return `You are a sales assistant for Cattelan Italia's SKORPIO + BELINDA configurator (Italian dining-room furniture). You help dealers and end-customers build a project (a "project" = a set of items: tables and/or matching chairs).

# Valid sizeKey values per collection — USE EXACTLY (no guessing)

${buildSizeReference()}

  Chairs (BL, BLM):  reg | B  (B = with armrests)

If the user asks for a size that doesn't exist in the chosen collection, pick the closest available — e.g. user asks "200×100 Keramik" → KR has 200x106, propose that.

# Product knowledge — tables (SKORPIO range, 9 sub-collections)

| Code | Name | Top family |
|------|------|------------|
| CL   | SKORPIO classic | clear / extra-clear / bevelled / MIST glass |
| CA   | SKORPIO Crystalart | printed bevelled glass (CY01, CY02) |
| KR   | SKORPIO Keramik | 16 ceramic codes (KM02..KS23) |
| KP   | SKORPIO Keramik Premium | 14 ceramics, thick top with brushed bronze/grey rim (BBR/BGR profile) |
| KPR  | SKORPIO Keramik Premium Round | ø160 only — **no EUR price, surfaces as "Price on request"** |
| KRD  | SKORPIO Keramik Round | ø140 or ø158 |
| KW   | SKORPIO Ker-Wood Round | wood (NC/RB) + ceramic insert; plain or T (swiveling) |
| RND  | SKORPIO Round | clear / bevelled glass, or NC/RB wood (ø140 wood: tier-A bases ONLY) |
| WD   | SKORPIO Wood | S irregular edge / B barrel / M Masterwood boat / Y dual-tone |

Base = X-shape sculptural steel. 7 finishes in 3 tiers:
- A (cheapest): GFM69 graphite, GFM73 black
- B: GFM11 titanium, GFM18 bronze, GFM70 pearl
- C (premium): BBR brushed bronze, BGR brushed grey

# Product knowledge — chairs (BELINDA)

| Code | Name | Frame |
|------|------|-------|
| BL   | BELINDA | solid ash wood (frN natural / frNC walnut stain / frRB burned-oak stain / fr73 matt black) |
| BLM  | BELINDA ML | embossed lacquered steel (GFM11/18/69/73 — no GFM70 / BBR / BGR) |

Each chair has 2 sizes: \`reg\` without armrests, \`B\` with armrests.
Upholstery — 4 pricing groups, all colour codes priced equally within their group:
- synthetic_leather: ECP01..ECP24, plus COM (customer-own material)
- fabric_micronubuck: B240..B932 fabric + MN103..MN320 micro-nubuck + COL (customer-own)
- soft_leather: numeric codes 891..996
- soft_leather_glove: GLV02..GLV23 (premium)

# Pairing guidance

- BLM (metal-frame chairs) → naturally pairs with CL / CA / KR / KP / RND-glass SKORPIO tables (shared GFM* metal codes).
- BL (wood-frame chairs) → pairs with WD (wood tops) or RND wood.
- Dining-set qty heuristic: 4 chairs for 150×150 / 160×90, 6 chairs for 200/240, 8 for 300, 10 for 320.

# How to act

- Use the tools to mutate state. Don't just describe; execute.
- Per turn: 1-2 sentences, then call tools. Suggest at most 1 alternative.
- When adding items, call \`select_item\` afterwards so the user sees what changed.
- Prices in EUR (trade, excl VAT). Note: KPR has no EUR price — quote "on request".
- Cite catalog codes when helpful (e.g. "BBR brushed bronze", "KM21 Colosseo").
- User-facing language: ${lang === 'it' ? 'Italian' : lang === 'uk' ? 'Ukrainian' : 'English'}.

${stateSummary}`;
}

// ─── Component ─────────────────────────────────────────────────────────────

export default function AICopilot({
  project, items, selectedItemId, total, lang, fmtEUR,
  addTable, addChair, updateItem, removeItem, selectItem,
}) {
  const [messages, setMessages] = useState([]);     // UI chat history (rendered)
  const [apiHistory, setApiHistory] = useState([]); // FULL Anthropic-API messages with tool_use/tool_result pairs preserved between turns
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const scrollerRef = useRef(null);

  // Snapshot getItems via ref so the validator can read the freshest list
  // without re-creating the dispatcher on every state change.
  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const executeTool = useCallback(
    makeToolExecutor({
      addTable, addChair, updateItem, removeItem, selectItem,
      getItems: () => itemsRef.current,
    }),
    [addTable, addChair, updateItem, removeItem, selectItem],
  );

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, loading]);

  const send = useCallback(async () => {
    const userText = input.trim();
    if (!userText || loading) return;

    setError(null);
    setInput('');
    setLoading(true);

    const stateSummary = buildStateSummary({ project, items, selectedItemId, total, fmtEUR });
    const systemPrompt = buildSystemPrompt(stateSummary, lang);

    // Start from previously-completed API history (with all tool_use/tool_result
    // pairs intact) and append the new user message.
    const apiMessages = [...apiHistory, { role: 'user', content: userText }];

    // Echo user msg into UI.
    setMessages(prev => [...prev, { role: 'user', text: userText }]);

    try {
      for (let iter = 0; iter < MAX_TOOL_ITERATIONS; iter++) {
        const resp = await fetch('/api/copilot', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            model: MODEL,
            max_tokens: 1024,
            system: systemPrompt,
            tools: TOOLS,
            messages: apiMessages,
          }),
        });
        if (!resp.ok) {
          const text = await resp.text();
          throw new Error(`API ${resp.status}: ${text.slice(0, 200)}`);
        }
        const data = await resp.json();
        apiMessages.push({ role: 'assistant', content: data.content });

        const textBlocks = (data.content || []).filter(b => b.type === 'text');
        const text = textBlocks.map(b => b.text).join('\n\n').trim();
        if (text) {
          setMessages(prev => [...prev, { role: 'assistant', text, apiContent: data.content }]);
        }

        const toolUses = (data.content || []).filter(b => b.type === 'tool_use');
        if (data.stop_reason !== 'tool_use' || toolUses.length === 0) break;

        const toolResults = toolUses.map(tu => ({
          type: 'tool_result',
          tool_use_id: tu.id,
          content: JSON.stringify(executeTool(tu.name, tu.input || {})),
        }));
        apiMessages.push({ role: 'user', content: toolResults });

        // Surface a compact "action" line in the UI.
        const actions = toolUses.map(tu => {
          const argsStr = Object.entries(tu.input || {})
            .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`)
            .join(', ');
          return `· ${tu.name}(${argsStr})`;
        });
        if (actions.length) {
          setMessages(prev => [...prev, { role: 'tool', text: actions.join('\n') }]);
        }
      }
      // Persist the COMPLETE message chain (including tool_use ↔ tool_result
      // pairs) for the next user turn.
      setApiHistory(apiMessages);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [input, loading, apiHistory, project, items, selectedItemId, total, lang, fmtEUR, executeTool]);

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="cfg-copilot">
      <div className="cfg-copilot-messages" ref={scrollerRef}>
        {messages.map((m, i) => (
          <div key={i} className={`cfg-copilot-msg cfg-copilot-${m.role}`}>
            <div className="cfg-copilot-bubble">{m.text}</div>
          </div>
        ))}
        {loading && <div className="cfg-copilot-msg cfg-copilot-assistant"><div className="cfg-copilot-bubble cfg-copilot-thinking">{t('copilot.thinking', lang)}</div></div>}
        {error && <div className="cfg-copilot-error">{error}</div>}
      </div>
      <div className="cfg-copilot-input">
        <textarea
          rows={2}
          placeholder={t('copilot.placeholder', lang)}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKey}
          disabled={loading}
        />
        <button className="cfg-btn" onClick={send} disabled={loading || !input.trim()}>
          <Send size={14} />
        </button>
      </div>
    </div>
  );
}
